import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * Server-driven minimum-app-version gate (#80).
 *
 * At start-up the app reports its build version to /api/app-version/minimum; if the
 * server says it's below the floor, we route to a blocking "update required" screen.
 * Pairs with the force-update lever (revoking refresh tokens server-side, per the
 * #79 revocation table): the lever drops an old build to the login screen, this gate
 * stops it from getting that far.
 *
 * Fails open on every error path — a version check must NEVER be what keeps a paying
 * user out of the app. If the plugin is absent (dev/browser), the server is
 * unreachable, or the response is malformed, the app proceeds normally.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient, private router: Router, private zone: NgZone) { }

  checkMinVersion(): void {
    this.getAppVersion().then((current) => {
      const query = current ? `?current=${encodeURIComponent(current)}` : '';
      this.http.get(`${this.baseUrl}/api/app-version/minimum${query}`).subscribe(
        (res: any) => {
          if (res && res.updateRequired) {
            this.zone.run(() =>
              this.router.navigate(['/auth/update-required'], { state: { info: res } }));
          }
        },
        () => { /* fail open: never block on a failed/absent version check */ });
    }).catch(() => { /* fail open */ });
  }

  /** cordova-plugin-app-version returns the build version; absent ⇒ null (fail open). */
  private getAppVersion(): Promise<string> {
    const cordova: any = (window as any).cordova;
    const appVersion = cordova && cordova.getAppVersion;
    if (appVersion && typeof appVersion.getVersionNumber === 'function') {
      return appVersion.getVersionNumber();
    }
    return Promise.resolve(null);
  }
}
